import { useEffect, useLayoutEffect, useMemo } from 'react';
import { LibraryModel, seedLibraryDocuments, seedLibraryScenarios } from '../models/LibraryModel';

/**
 * The Library's model for the signed-in user in the active organisation.
 *
 * It lives for the session, so the page restores the tab, filters, view and
 * search on return. It's rebuilt when the user or the active organisation
 * changes: an org switch and a logout don't reload the app, and one org's
 * documents must never show in another's once the API fills the model. A
 * replaced model stops its upload timer rather than ticking on unseen.
 *
 * Scenarios (PRD-1701) exist only while `scenariosEnabled`
 * (`navy-assessments`), so with it off no document reads as in use. A new
 * model starts with the flag as it is; after that the flag switches them on
 * the same model rather than rebuilding it, so a flag that resolves or changes
 * mid-session never loses the Library's documents, uploads or filters, nor the
 * scenarios saved this session.
 */
export const useLibraryModel = (me, scenariosEnabled = false) => {
  const scope = me ? `${me.id}:${me.org_id}` : null;
  const model = useMemo(
    () => new LibraryModel(seedLibraryDocuments(), { scenarios: scenariosEnabled ? seedLibraryScenarios() : null }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuilt per scope on purpose; the flag only seeds
    [scope],
  );
  useLayoutEffect(() => { model.setScenariosOn(scenariosEnabled); }, [model, scenariosEnabled]);
  useEffect(() => () => model.stopUploads(), [model]);
  return model;
};
